export interface PartPosition {
    id: number;
    /** Viewport-relative top/bottom of the part card (getBoundingClientRect). */
    top: number;
    bottom: number;
}

export interface ActivePartOptions {
    /** The page is scrolled to its end. */
    atBottom?: boolean;
    viewportHeight?: number;
}

/**
 * The part the user is looking at while scrolling, given the cards in page order.
 *
 * A part becomes active once its top crosses `referenceY` (the middle of the visible
 * area, matching "Go to part", which centers the selected part). Before any part
 * reaches the line, the first part is active. At the end of the page the last visible
 * part wins, because short trailing parts can never reach the line.
 */
export const getActivePartId = (
    parts: Array<PartPosition>,
    referenceY: number,
    { atBottom = false, viewportHeight }: ActivePartOptions = {}
): number | null => {
    if (!parts.length) {
        return null;
    }

    if (atBottom && viewportHeight != null) {
        const visible = parts.filter(part => part.top < viewportHeight && part.bottom > 0);
        if (visible.length) {
            return visible[visible.length - 1].id;
        }
    }

    let activeId = parts[0].id;
    for (const part of parts) {
        if (part.top > referenceY) {
            break;
        }
        activeId = part.id;
    }
    return activeId;
};

/** Middle of the area below the (possibly sticky) navigator. */
export const getScrollReferenceY = (navigatorBottom: number | undefined, viewportHeight: number) => {
    const top = Math.max(navigatorBottom ?? 0, 0);
    return top + Math.max(viewportHeight - top, 0) / 2;
};
